/**
 * The legal documents the footer links to.
 *
 * ── WHY `GET /api/consent/documents` AND NOT `GET /api/cms/legal/:slug` ─────
 * Both serve legal text, and they are not the same text. The CMS route serves
 * the LIVE, editable page, which an operator can change at any moment. The
 * consent route serves the FROZEN, versioned snapshot whose content hash is
 * what a player's consent record actually points at. Linking the footer at the
 * CMS copy would mean the document a player can read and the document they are
 * recorded as having accepted are two different strings that merely usually
 * agree — and the one time they disagree is the one time it matters.
 *
 * So: the footer lists what has been PUBLISHED. That has a visible
 * consequence, and it is the correct one — until an admin POSTs
 * `/api/admin/legal-versions`, there are no published documents and the footer
 * shows only the index link. Editing the CMS page is not publishing.
 *
 * The endpoint is public (the registration screen needs it before an account
 * exists), so a signed-out visitor on the landing page gets the same list.
 */
import { api } from '../../core/network/api';

/** One published legal document, reduced to what a link needs. */
export interface LegalDocumentLink {
  readonly slug: string;
  readonly title: string;
  readonly version: number;
}

/**
 * Where the two documents every site has go in the list.
 *
 * Not alphabetical: "Privacy Policy" before "Terms of Service" is alphabetical
 * and wrong — the terms are the agreement, the privacy policy explains one
 * part of it, and every site on the internet lists them in that order. Anything
 * an operator publishes beyond these two follows, ordered by title.
 */
const SLUG_PRIORITY: Record<string, number> = { terms: 0, privacy: 1 };

function priorityOf(slug: string): number {
  return SLUG_PRIORITY[slug] ?? 100;
}

/** Parse the endpoint's payload defensively. Unknown shapes yield an empty list. */
export function parseLegalDocuments(raw: unknown): readonly LegalDocumentLink[] {
  if (typeof raw !== 'object' || raw === null) return [];
  const documents = (raw as Record<string, unknown>).documents;
  if (!Array.isArray(documents)) return [];

  const bySlug = new Map<string, LegalDocumentLink>();
  for (const entry of documents) {
    if (typeof entry !== 'object' || entry === null) continue;
    const record = entry as Record<string, unknown>;
    const slug = typeof record.slug === 'string' ? record.slug : '';
    const title = typeof record.title === 'string' ? record.title : '';
    if (slug === '' || title === '') continue;
    const version = typeof record.version === 'number' ? record.version : 0;
    // One purpose per slug is the service's invariant, but two purposes CAN
    // name the same document; keep the highest version and list it once.
    const existing = bySlug.get(slug);
    if (existing === undefined || version > existing.version) {
      bySlug.set(slug, { slug, title, version });
    }
  }

  return [...bySlug.values()].sort(
    (a, b) => priorityOf(a.slug) - priorityOf(b.slug) || a.title.localeCompare(b.title),
  );
}

let cached: readonly LegalDocumentLink[] | null = null;
let inFlight: Promise<readonly LegalDocumentLink[]> | null = null;

/**
 * The published documents. Never rejects — a footer that throws because the
 * legal endpoint was slow is a worse outcome than a footer with one fewer
 * link, and the `/legal` index link is rendered unconditionally either way.
 */
export async function loadLegalDocuments(): Promise<readonly LegalDocumentLink[]> {
  if (cached !== null) return cached;
  if (inFlight !== null) return inFlight;

  inFlight = api
    .get<unknown>('/consent/documents')
    .then((raw) => parseLegalDocuments(raw))
    .catch(() => [] as readonly LegalDocumentLink[])
    .then((documents) => {
      cached = documents;
      inFlight = null;
      return documents;
    });
  return inFlight;
}

/** The cached list, or `null` when nothing has resolved yet. Never fetches. */
export function peekLegalDocuments(): readonly LegalDocumentLink[] | null {
  return cached;
}

/** The route a legal document is read at. Owned by whoever builds `/legal`. */
export function legalDocumentPath(slug: string): string {
  return `/legal/${encodeURIComponent(slug)}`;
}

/** Test seam: drop the module-level cache. */
export function resetLegalDocumentsCacheForTests(): void {
  cached = null;
  inFlight = null;
}
